import { Injectable, inject } from "@angular/core";
import {
	Router,
	NavigationStart,
	NavigationEnd,
	NavigationCancel,
	NavigationError,
} from "@angular/router";
import { BehaviorSubject, Observable, filter } from "rxjs";
import { LoadingService } from "./loading.service";

export interface LoadingContext {
	isLoading: boolean;
	message?: string;
	type: "route" | "general";
}

@Injectable({
	providedIn: "root",
})
export class RouterLoadingService {
	private router = inject(Router);
	private loadingService = inject(LoadingService);

	private routeLoadingSubject = new BehaviorSubject<LoadingContext>({
		isLoading: false,
		type: "route",
	});
	public routeLoading$: Observable<LoadingContext> = this.routeLoadingSubject.asObservable();

	constructor() {
		this.setupRouterEvents();
	}

	private setupRouterEvents(): void {
		this.router.events
			.pipe(
				filter(
					(event) =>
						event instanceof NavigationStart ||
						event instanceof NavigationEnd ||
						event instanceof NavigationCancel ||
						event instanceof NavigationError,
				),
			)
			.subscribe((event) => {
				if (event instanceof NavigationStart) {
					this.startRouteLoading(event.url);
				} else if (
					event instanceof NavigationEnd ||
					event instanceof NavigationCancel ||
					event instanceof NavigationError
				) {
					this.stopRouteLoading();
				}
			});
	}

	private startRouteLoading(url: string): void {
		const message = this.getLoadingMessage(url);
		this.routeLoadingSubject.next({
			isLoading: true,
			message,
			type: "route",
		});
		this.loadingService.startLoading();
	}

	private stopRouteLoading(): void {
		// Add a delay to allow components to start their own loading
		setTimeout(() => {
			this.routeLoadingSubject.next({
				isLoading: false,
				type: "route",
			});
			this.loadingService.stopLoading();
		}, 500);
	}

	private getLoadingMessage(url: string): string {
		// Provide contextual loading messages based on the route
		if (url.includes("/partners")) {
			return "Loading partner data...";
		} else if (url.includes("/organizations")) {
			return "Loading organization data...";
		} else if (url.includes("/global-management")) {
			return "Loading global management...";
		} else if (url.includes("/cohorts")) {
			return "Loading cohorts...";
		} else if (url.includes("/questionnaires")) {
			return "Loading questionnaires...";
		} else if (url.includes("/profile")) {
			return "Loading profile...";
		} else if (url.includes("/dashboard") || url.includes("/home")) {
			return "Loading dashboard...";
		}
		return "Loading...";
	}

	public getRouteLoadingState(): boolean {
		return this.routeLoadingSubject.value.isLoading;
	}
}
